import ko from "knockout";
import moment from "moment";
import view from "./monthDate.html";
import createEnabler from "flow/koHelpers/createEnablerDisabler";
import Popper from "popper.js";
import { getMonthListShort } from "flow/localization/localization";
import config from "flow/config";
import { makeObservableIfNeeded } from "flow/koHelpers/makeObservable";
import { Dictionary } from "flow/api/types";
import { MaybeObservable } from "flow/interfaces/MaybeObservable";
import createRequiredObservable from "flow/koHelpers/createRequiredObservable";
import { selectIdAttr } from "flow/koHelpers/selectId";
import { selectLabel } from "flow/koHelpers/selectParam";

export interface IDateMonthParams {
    value: KnockoutObservable<string>;
    attr: MaybeObservable<any>;
    disable?: MaybeObservable<boolean>;
    enable?: MaybeObservable<boolean>;
    minYear?: MaybeObservable<number>;
    maxYear?: MaybeObservable<number>;
    minMonth?: MaybeObservable<number>;
    maxMonth?: MaybeObservable<number>;
    validationElement: KnockoutObservable<string>;
    monthStartsOnOne?: boolean;
    noFutureMonth?: boolean;
    meta?: MaybeObservable<string>;
    text?: MaybeObservable<string>;
    metaPlaceholder?: MaybeObservable<string>;
    placeholder?: MaybeObservable<string>;
}

class MonthPickerViewModel {
    value: KnockoutObservable<string>;
    validationElement: KnockoutObservable<string>;
    enable: MaybeObservable<boolean>;
    popper: Popper;
    isPickerVisible = ko.observable(false);
    attributes: Dictionary;
    label: KnockoutObservable<string>;
    id: KnockoutObservable<string>;
    required: KnockoutObservable<boolean>;

    year = ko.observable<number>();
    private minYear: KnockoutObservable<number>;
    private maxYear: KnockoutObservable<number>;

    years: KnockoutComputed<number[]>;

    month = ko.observable<number>();
    private minMonth: KnockoutObservable<number>;
    private maxMonth: KnockoutObservable<number>;

    months: KnockoutComputed<number[]>;

    monthNames = getMonthListShort();

    private subscriptions: KnockoutSubscription[] = [];

    constructor(
        params: IDateMonthParams,
        private koMonthDateElement: HTMLElement,
        private input: HTMLInputElement,
        monthPickerElement: HTMLElement
    ) {
        if (!ko.isObservable(params.value)) {
            throw new Error("ko-month-date params value must be observable!");
        }

        const date = new Date();
        this.required = createRequiredObservable(params.value);
        this.id = selectIdAttr("ko_monthdate", params.attr);
        this.enable = createEnabler(params.enable, params.disable);
        this.label = selectLabel(params.meta, params.text);
        const defaultMinYear = date.getFullYear() - 10;
        const defaultMaxYear = params.noFutureMonth ? date.getFullYear() : date.getFullYear() + 10;
        const defaultMinMonth = 0;
        const defaultMaxMonth = params.noFutureMonth ? date.getMonth() : 11;

        this.minYear = makeObservableIfNeeded(params.minYear, defaultMinYear);
        this.maxYear = makeObservableIfNeeded(params.maxYear, defaultMaxYear);

        this.years = ko.pureComputed<number[]>(() => {
            return Array.from({ length: this.maxYear() - this.minYear() + 1 }, (_, i) => i + this.minYear());
        });

        if (params.monthStartsOnOne) {
            this.minMonth = ko.pureComputed(() =>
                params.minMonth !== undefined ? ko.unwrap(params.minMonth) - 1 : defaultMinMonth
            );
            this.maxMonth = ko.pureComputed(() =>
                params.maxMonth !== undefined ? ko.unwrap(params.maxMonth) - 1 : defaultMaxMonth
            );
        } else {
            this.minMonth = makeObservableIfNeeded(params.minMonth, defaultMinMonth);
            this.maxMonth = makeObservableIfNeeded(params.maxMonth, defaultMaxMonth);
        }

        this.months = ko.pureComputed<number[]>(() => {
            const min = this.year() === this.minYear() ? this.minMonth() : 0;
            const max = this.year() === this.maxYear() ? this.maxMonth() : 11;

            return Array.from({ length: max - min + 1 }, (_, i) => i + min);
        });

        this.value = params.value;

        this.subscriptions.push(this.value.subscribe(this.updateDropdownValues));

        if (this.value()) {
            this.updateDropdownValues(this.value());
        } else {
            this.year(date.getFullYear());
            this.month(date.getMonth());
        }

        this.subscriptions.push(this.year.subscribe(this.onYearUpdate));

        this.validationElement = params.validationElement || params.value;

        this.enable = createEnabler(params.enable, params.disable);

        this.attributes = Object.assign(
            {
                autocomplete: "off"
            },
            ko.unwrap(params.attr)
        );

        this.popper = new Popper(input, monthPickerElement, {
            placement: "bottom-start"
        });
    }

    updateDropdownValues = (newVal: string) => {
        const mom = moment(newVal);
        this.year(mom.year());
        this.month(mom.month());
    };

    private onYearUpdate = (newYear: number) => {
        if (newYear === this.minYear() && this.month() < this.minMonth()) {
            this.month(this.minMonth());
        }

        if (newYear === this.maxYear() && this.month() > this.maxMonth()) {
            this.month(this.maxMonth());
        }
    };

    onKeyDown = (event: KeyboardEvent) => {
        if (event.key === config.keyCodes.ESCAPE) {
            this.removeEventListeners();
            this.isPickerVisible(false);
            this.updateDropdownValues(this.value());

            event.stopPropagation();
        }
    };

    onAnyClick = (event: Event) => {
        if (event.target && this.koMonthDateElement.contains(event.target as Node)) {
            return;
        }
        this.hideAndSave();
    };

    onFocusChanged = (event: FocusEvent) => {
        if (event.target && this.koMonthDateElement.contains(event.target as Node)) {
            return;
        }
        this.hideAndSave();
    };

    private hideAndSave = () => {
        this.removeEventListeners();
        this.isPickerVisible(false);

        this.value(moment([this.year(), this.month()]).format("YYYY-MM"));
    };

    addEventListeners = () => {
        window.addEventListener("click", this.onAnyClick);
        window.addEventListener("focusin", this.onFocusChanged);
        this.koMonthDateElement.addEventListener("keydown", this.onKeyDown);
    };

    removeEventListeners = () => {
        window.removeEventListener("click", this.onAnyClick);
        window.removeEventListener("focusin", this.onFocusChanged);
        this.koMonthDateElement.removeEventListener("keydown", this.onKeyDown);
    };

    onClickButton = () => {
        if (this.isPickerVisible()) {
            return;
        }

        this.isPickerVisible(true);
        this.addEventListeners();

        this.input.focus();
    };

    onPrev = () => {
        if (this.year() === this.minYear()) {
            if (this.month() > this.minMonth()) {
                this.month(this.month() - 1);
            } else {
                return; // Already as low as we can go
            }
        } else {
            if (this.month() > 0) {
                this.month(this.month() - 1);
            } else {
                // Shift year, start in December
                this.year(this.year() - 1);
                this.month(11);
            }
        }
    };

    onNext = () => {
        if (this.year() === this.maxYear()) {
            if (this.month() < this.maxMonth()) {
                this.month(this.month() + 1);
            } else {
                return; // Already as high as we can go
            }
        } else {
            if (this.month() < 11) {
                this.month(this.month() + 1);
            } else {
                // Shift year, start in January
                this.year(this.year() + 1);
                this.month(0);
            }
        }
    };

    getNameOfMonth = (month: number) => {
        return this.monthNames[month];
    };

    dispose = () => {
        this.subscriptions.forEach(s => s.dispose());
        this.removeEventListeners();
        this.isPickerVisible(false);
        this.popper.destroy();
    };
}

ko.components.register("ko-month-date", {
    viewModel: {
        createViewModel(params: any, componentInfo: KnockoutComponentTypes.ComponentInfo) {
            const koMonthDateElement = componentInfo.element as HTMLElement;
            const input = koMonthDateElement.querySelector("input") as HTMLInputElement;
            const monthPickerElement = koMonthDateElement.querySelector(".lc-month-picker") as HTMLElement;

            return new MonthPickerViewModel(params, koMonthDateElement, input, monthPickerElement);
        }
    },
    template: view,
    synchronous: true
});
